<template>
  <div class="reclaiming-life">
    <div class="reclaiming-life__container">
      <h2 class="reclaiming-life__title">
        {{ $t('b2b__landing_page__feature_highlights__title') }}
      </h2>

      <div class="reclaiming-life__grid">
        <article class="reclaiming-life__card reclaiming-life__card--large">
          <div class="reclaiming-life__large-content">
            <h3
              class="reclaiming-life__card-title"
              v-html="
                $t('b2b__landing_page__feature_highlights__card_one__title')
              "
            />

            <p class="reclaiming-life__card-description">
              {{
                $t(
                  'b2b__landing_page__feature_highlights__card_one__description',
                )
              }}
            </p>
          </div>

          <div class="reclaiming-life__large-media">
            <img
              :src="appImage"
              :alt="
                plainText(
                  $t('b2b__landing_page__feature_highlights__card_one__title'),
                )
              "
              class="reclaiming-life__image reclaiming-life__image--app"
            />
          </div>
        </article>

        <div class="reclaiming-life__right-column">
          <div class="reclaiming-life__small-cards">
            <article class="reclaiming-life__card reclaiming-life__card--small">
              <h3
                class="reclaiming-life__card-title"
                v-html="
                  $t('b2b__landing_page__feature_highlights__card_three__title')
                "
              />

              <p class="reclaiming-life__card-description">
                {{
                  $t(
                    'b2b__landing_page__feature_highlights__card_three__description',
                  )
                }}
              </p>
            </article>

            <article class="reclaiming-life__card reclaiming-life__card--small">
              <h3
                class="reclaiming-life__card-title"
                v-html="
                  $t('b2b__landing_page__feature_highlights__card_four__title')
                "
              />

              <p class="reclaiming-life__card-description">
                {{
                  $t(
                    'b2b__landing_page__feature_highlights__card_four__description',
                  )
                }}
              </p>
            </article>
          </div>

          <article class="reclaiming-life__card reclaiming-life__card--expert">
            <div class="reclaiming-life__expert-content">
              <h3
                class="reclaiming-life__card-title"
                v-html="
                  $t('b2b__landing_page__feature_highlights__card_two__title')
                "
              />

              <p class="reclaiming-life__card-description">
                {{
                  $t(
                    'b2b__landing_page__feature_highlights__card_two__description',
                  )
                }}
              </p>
            </div>

            <div class="reclaiming-life__expert-media">
              <img
                :src="coachImage"
                :alt="
                  plainText(
                    $t(
                      'b2b__landing_page__feature_highlights__card_two__title',
                    ),
                  )
                "
                class="reclaiming-life__image reclaiming-life__image--coach"
              />
            </div>
          </article>
        </div>
      </div>

      <BaseButton
        class="reclaiming-life__cta"
        @click="handleCTAButtonClick"
      >
        {{ ctaBtnTitle }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup>
import BaseButton from '@shared/components/BaseButton.vue'
import appImage from '@shared/assets/img/reclaiming-life-highlight.png'
import coachImage from '@shared/assets/img/feature-highlights-expert-trimmed.png'

defineProps({
  ctaBtnTitle: {
    type: String,
    default: '',
  },
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  variant: {
    type: String,
    default: '',
  },
})

const plainText = (text) => text.replace(/<[^>]*>/g, '')
</script>

<style lang="scss" scoped>
.reclaiming-life {
  background-color: $kds-color-background-blue-subtle;
  padding: $kds-space-6x 10px;

  @include media-up(lg) {
    padding: 72px 10px;
  }

  &__container {
    @extend %container;
    margin: auto;
    max-width: 1200px;
  }

  &__title {
    @include kds-typography-display-small;
    font-weight: $kds-typography-font-weight-medium;
    text-align: center;
    margin: 0 auto;
    max-width: 720px;

    @include media-up(md) {
      @include kds-typography-display-medium;
      font-weight: $kds-typography-font-weight-medium;
    }
  }

  &__grid {
    margin-top: $kds-space-6x;
    display: grid;
    gap: 16px;

    @include media-up(md) {
      grid-template-columns: 1fr;
      margin-top: $kds-space-4x;
    }

    @include media-up(lg) {
      grid-template-columns: minmax(0, 400px) minmax(0, 1fr);
      margin-top: $kds-space-4x;
    }
  }

  &__right-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__small-cards {
    display: grid;
    gap: 16px;

    @include media-up(md) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &__card {
    background-color: $kds-color-background-base;
    border-radius: 24px;
    padding: $kds-space-2_5x;
    display: flex;
    flex-direction: column;

    @include media-up(md) {
      border-radius: 32px;
      padding: $kds-space-4x;
    }

    &--large {
      justify-content: flex-start;
      min-height: 315px;
      padding-left: 0;
      padding-right: 0;
      overflow: hidden;

      @include media-up(md) {
        min-height: 480px;
      }

      .reclaiming-life__card-title,
      .reclaiming-life__card-description {
        padding-left: $kds-space-2_5x;
        padding-right: $kds-space-2_5x;

        @include media-up(md) {
          padding-left: $kds-space-4x;
          padding-right: $kds-space-4x;
        }
      }

      .reclaiming-life__image--app {
        margin-top: auto;
      }

      @include media-between(md, tablet-max) {
        min-height: 340px;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
        column-gap: $kds-space-2x;

        .reclaiming-life__image--app {
          margin-top: 0;
        }
      }
    }

    &--expert {
      gap: 16px;
      overflow: hidden;

      @include media-up(md) {
        min-height: 220px;
        gap: 24px;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
      }

      @include media-up(lg) {
        order: -1;
      }
    }

    &--small {
      @include media-up(md) {
        min-height: 196px;
      }
    }

    &:has(> img:last-child),
    &:has(> :last-child > img) {
      padding-bottom: 0;
    }
  }

  &__card-title {
    @include kds-typography-title-xlarge;
    font-weight: $kds-typography-font-weight-regular;
    margin: 0;
    text-align: center;

    @include media-up(md) {
      @include kds-typography-display-small;
      font-weight: $kds-typography-font-weight-regular;
      text-align: left;
    }

    :deep(strong) {
      font-weight: $kds-typography-font-weight-bold;
    }
  }

  &__card-description {
    @include kds-typography-paragraph-large;
    margin: $kds-space-1_5x 0 0;
    color: $kds-color-text-secondary;
    text-align: center;

    @include media-up(md) {
      text-align: left;
    }
  }

  &__expert-content {
    @include media-up(md) {
      flex: 1;
      min-width: 0;
      max-width: none;
    }
  }

  &__expert-media {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    min-height: 140px;

    @include media-up(md) {
      justify-content: flex-end;
      min-height: 100%;
      min-width: 200px;
      flex: 0 0 200px;
    }
  }

  &__large-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
  }

  &__large-media {
    display: flex;
    justify-content: center;
    align-items: flex-end;

    @include media-between(md, tablet-max) {
      justify-content: flex-end;
      min-height: 100%;
      min-width: 400px;
      flex: 0 0 400px;
    }
  }

  &__image {
    &--app {
      display: block;
      width: 100%;
      max-width: none;
      align-self: stretch;
      object-fit: contain;

      @include media-between(md, tablet-max) {
        width: 100%;
        height: 100%;
        max-height: none;
        min-height: 0;
      }
    }

    &--coach {
      width: auto;
      max-width: 100%;
      object-fit: contain;
      align-self: flex-end;
      height: 170px;

      @include media-up(md) {
        width: 100%;
        height: auto;
        max-height: 220px;
        min-height: 0;
      }
    }
  }

  &__cta {
    width: fit-content;
    margin: 60px auto 0;
  }

  @include media-between(lg, tablet-max) {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__small-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__card {
      &--expert {
        order: 0;
      }
    }
  }
}
</style>
